import { useEffect, useState } from "react";
import {
  Icon,
  IconButton,
  Paper,
  TableRow,
  TableCell,
  useTheme,
} from "@mui/material";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { ENVIRONMENT } from "../../environment/environment";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { fornecedorService } from "./service/fornecedor.service";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { formatarCpfCnpj, toSlug } from "../../shared/utils";

export function ListagemFornecedores() {
  const [fornecedores, setFornecedores] = useState<any[]>([]);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState<string | undefined>(undefined);

  const buscaDebounce = useDebounce(query || "", 500);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const reload = location.state?.reload;

  useEffect(() => {
    const normalizarDocumento = (valor: string) => valor.replace(/[^\d]/g, "");

    const gerarQuery = () => {
      if (!busca || busca.trim() === "") return undefined;

      const valorLimpo = normalizarDocumento(busca);
      const isNumerico = /^\d+$/.test(valorLimpo);

      if (isNumerico) {
        if (valorLimpo.length === 11) {
          return `campo_pesquisa=cpf&tipo_pesquisa=in&valor_pesquisa=${valorLimpo}`;
        } else if (valorLimpo.length >= 14) {
          return `campo_pesquisa=cnpj&tipo_pesquisa=in&valor_pesquisa=${valorLimpo}`;
        }
      }

      return `campo_pesquisa=slug&tipo_pesquisa=in&valor_pesquisa=${toSlug(
        busca
      )}`;
    };

    setQuery(gerarQuery());
  }, [busca]);

  const carregarFornecedores = async (
    query?: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    try {
      const result = await fornecedorService.findAll(query, pagina, limite);
      setFornecedores(result.items || []);
      setTotalCount(result?.meta?.totalItems || 0);
      setIsLoading(false);
    } catch (error) {
      toastError(error);
      setFornecedores([]);
    }
  };

  useEffect(() => {
    if (reload) {
      setIsLoading(true);
      carregarFornecedores(undefined, 0);
    }
  }, [reload]);

  useEffect(() => {
    setPage(0);
    carregarFornecedores(buscaDebounce, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarFornecedores(buscaDebounce, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarFornecedores(buscaDebounce, 0, newLimit);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o fornecedor?`)) {
      try {
        await fornecedorService.deletar(id);
        toast.success(`Fornecedor excluído com sucesso!`);
        carregarFornecedores(buscaDebounce, page, rowsPerPage);
      } catch (error) {
        toastError(error);
      }
    }
  };

  return (
    <BaseLayout
      title="Fornecedores"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por nome, razão social, cpf e cnpj"}
          textoBotaoNovo="Adicionar Novo"
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() => navigate("/fornecedores/criar")}
        />
      }
    >
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          border: "1px solid #ccc",
          borderRadius: 2,
        }}
      >
        <TabelaDaListagem
          ariaLabel="tabela de fornecedores"
          headTable={[
            "Nome Fantasia",
            "Razão Social",
            "Nome Completo",
            "CNPJ",
            "CPF",
            "Status",
            "Ações",
          ]}
          tableBody={
            fornecedores.length > 0 ? (
              fornecedores.map((fornecedor) => (
                <TableRow hover key={fornecedor.id}>
                  <TableCell>{fornecedor.fantasyName ?? "—"}</TableCell>
                  <TableCell>{fornecedor.corporateName ?? "—"}</TableCell>
                  <TableCell>{fornecedor.fullName ?? "—"}</TableCell>
                  <TableCell>{formatarCpfCnpj(fornecedor.cnpj) ?? "—"}</TableCell>
                  <TableCell>{formatarCpfCnpj(fornecedor.cpf) ?? "—"}</TableCell>
                  <TableCell>
                    {fornecedor.active ? "Ativo" : "Inativo"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/fornecedores/detalhe/${fornecedor.id}`)
                      }
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(fornecedor.id)}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhum fornecedor encontrado.
                </TableCell>
              </TableRow>
            )
          }
        />
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </BaseLayout>
  );
}